import { useState } from 'react'
import { toast } from 'sonner'
import { Lock, User, Users, Building2, Handshake, X, Search, Plus, Trash2 } from 'lucide-react'
import { Button } from '@/shared/components/ui/button'
import { Input } from '@/shared/components/ui/input'
import { Label } from '@/shared/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/shared/components/ui/dialog'
import {
  useAccessGrants,
  useGrantAccess,
  useRevokeAccess,
  useActorSearch,
} from '@/admin/hooks/use-acl'
import type { AccessGrant, ActorSearchResult } from '@/admin/hooks/use-acl'

import { toastApiError } from '@/shared/lib/api-error'
const ACTOR_ICONS: Record<string, typeof User> = {
  user: User,
  role: Users,
  organisation: Building2,
  partnership: Handshake,
}

const ACTOR_KIND_LABELS: Record<string, string> = {
  user: 'User',
  role: 'Role',
  organisation: 'Organisation',
  partnership: 'Partner',
}

/**
 * AccessLevelPanel - lock icon trigger that opens a "Manage access" dialog.
 *
 * Placed in the header area of detail pages (registrations, claims).
 * Allows tenants to review and manage row-level ACL grants for a resource.
 */
export function AccessLevelPanel({
  resourceType,
  objectId,
}: {
  resourceType: string
  objectId: number | null
}) {
  const [manageOpen, setManageOpen] = useState(false)
  const [grantOpen, setGrantOpen] = useState(false)
  const { data: grants = [], isPending: grantsPending, isFetching: grantsFetching } = useAccessGrants(resourceType, objectId)

  if (!objectId) return null

  return (
    <>
      {/* Lock icon trigger */}
      <Button
        onClick={() => setManageOpen(true)}
        size="default"
        className="w-8 cursor-pointer rounded-[6px] bg-[var(--green)] px-0 text-white hover:bg-[var(--green-hover)]"
        title="Manage access"
      >
        <Lock className="h-3.5 w-3.5" />
      </Button>
      {/* Manage access dialog */}
      <Dialog open={manageOpen} onOpenChange={setManageOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Manage access</DialogTitle>
          </DialogHeader>

          {/* + Grant access button */}
          <Button
            onClick={() => {
              setManageOpen(false)
              setGrantOpen(true)
            }}
            variant="outline"
            className="flex w-full cursor-pointer items-center justify-center gap-2 rounded-[4px] border-[0.5px] border-[var(--rule-strong)] bg-transparent py-2.5 text-[13px] text-[var(--green)] transition-all duration-150 hover:bg-[var(--bg-subtle)]"
          >
            <Plus className="h-3.5 w-3.5" />
            Grant access
          </Button>

          {/* Grants list */}
          {grantsPending || (grantsFetching && grants.length === 0) ? (
            <p className="py-4 text-center text-[12px] text-[var(--ink-3)]">
              Loading...
            </p>
          ) : grants.length === 0 ? (
            <p className="py-4 text-center text-[12px] text-[var(--ink-3)]">
              No access levels found
            </p>
          ) : (
            <div className="flex flex-col gap-1.5 max-h-[280px] overflow-y-auto">
              {grants.map((grant) => (
                <GrantRow
                  key={`${grant.actor_id}-${grant.permission}`}
                  grant={grant}
                  resourceType={resourceType}
                  objectId={objectId}
                />
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
      {/* Grant access dialog */}
      <GrantDialog
        open={grantOpen}
        onOpenChange={setGrantOpen}
        resourceType={resourceType}
        objectId={objectId}
        onGranted={() => {
          setGrantOpen(false)
          setManageOpen(true)
        }}
      />
    </>
  );
}

function GrantRow({
  grant,
  resourceType,
  objectId,
}: {
  grant: AccessGrant
  resourceType: string
  objectId: number
}) {
  const revokeAccess = useRevokeAccess()
  const Icon = ACTOR_ICONS[grant.actor_kind] || User
  const kindLabel = ACTOR_KIND_LABELS[grant.actor_kind] || grant.actor_kind

  function handleRevoke() {
    revokeAccess.mutate(
      {
        resource_type: resourceType,
        object_id: objectId,
        actor_id: grant.actor_id,
        permission: grant.permission,
      },
      {
        onSuccess: () => toast.success('Access revoked'),
        onError: (err) => toastApiError(err, 'Failed to revoke access'),
      }
    )
  }

  return (
    <div className="flex items-center justify-between py-2.5 px-1">
      <div className="flex items-center gap-3 min-w-0">
        <Icon className="h-4 w-4 shrink-0 text-[var(--ink-3)]" />
        <div className="min-w-0">
          <p className="truncate text-[13px] text-[var(--ink)]">
            {grant.label}
          </p>
          <p className="text-[10px] text-[var(--ink-3)]">
            {kindLabel}
          </p>
        </div>
      </div>
      <div className="flex items-center gap-3 shrink-0">
        <span className="rounded-[4px] border-[0.5px] border-[var(--rule-strong)] px-2.5 py-1 text-[11px] text-[var(--ink-2)]">
          {grant.permission === 'view' ? 'View Only' : 'Edit'}
        </span>
        <Button
          onClick={handleRevoke}
          disabled={revokeAccess.isPending}
          variant="ghost"
          size="icon-xs"
          className="cursor-pointer text-[var(--status-error)] transition-colors duration-150 disabled:opacity-40"
          title="Revoke access"
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}

function GrantDialog({
  open,
  onOpenChange,
  resourceType,
  objectId,
  onGranted,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  resourceType: string
  objectId: number
  onGranted: () => void
}) {
  const [search, setSearch] = useState('')
  const [selectedActor, setSelectedActor] = useState<ActorSearchResult | null>(null)
  const [permission, setPermission] = useState('view')
  const { data: actors = [] } = useActorSearch(search)
  const grantAccess = useGrantAccess()

  function handleGrant() {
    if (!selectedActor) return
    grantAccess.mutate(
      {
        resource_type: resourceType,
        object_id: objectId,
        actor_id: selectedActor.actor_id,
        permission,
      },
      {
        onSuccess: () => {
          toast.success('Access granted')
          resetForm()
          onGranted()
        },
        onError: (err) => toastApiError(err, 'Failed to grant access'),
      }
    )
  }

  function resetForm() {
    setSearch('')
    setSelectedActor(null)
    setPermission('view')
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (!v) resetForm()
        onOpenChange(v)
      }}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Grant access</DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          {/* Actor search */}
          <div>
            <Label className="clara-label mb-1.5 block text-[var(--ink-2)]">
              Actor
            </Label>
            {selectedActor ? (
              <div className="flex items-center justify-between rounded-[4px] border-[0.5px] border-[var(--rule-strong)] px-3 py-2">
                <div className="flex items-center gap-2">
                  {(() => {
                    const Icon = ACTOR_ICONS[selectedActor.actor_kind] || User
                    return <Icon className="h-3.5 w-3.5 text-[var(--ink-3)]" />
                  })()}
                  <span className="text-[13px] text-[var(--ink)]">
                    {selectedActor.label}
                  </span>
                </div>
                <Button
                  onClick={() => {
                    setSelectedActor(null)
                    setSearch('')
                  }}
                  variant="ghost"
                  size="icon-xs"
                  className="cursor-pointer text-[var(--ink-3)]"
                >
                  <X className="h-3.5 w-3.5" />
                </Button>
              </div>
            ) : (
              <div>
                <div className="relative">
                  <Input
                    placeholder="Start typing to search..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="text-[13px] h-9 pr-8"
                    autoFocus
                  />
                  <Search
                    className="absolute right-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-[var(--ink-3)]"
                  />
                </div>
                {search.length >= 2 && actors.length > 0 && (
                  <div className="mt-1 flex max-h-[140px] flex-col gap-0.5 overflow-y-auto rounded-[4px] border-[0.5px] border-[var(--rule)] bg-[var(--bg)] p-1">
                    {actors.map((actor) => {
                      const Icon = ACTOR_ICONS[actor.actor_kind] || User
                      return (
                        <Button
                          key={`${actor.actor_kind}-${actor.actor_id}`}
                          onClick={() => setSelectedActor(actor)}
                          variant="ghost"
                          className="flex h-auto items-center gap-2.5 px-2.5 py-2 rounded-[4px] text-left cursor-pointer transition-all duration-150 hover:bg-[var(--bg-subtle)]"
                        >
                          <Icon className="h-3.5 w-3.5 shrink-0 text-[var(--ink-3)]" />
                          <div className="min-w-0">
                            <span className="block truncate text-[12px] text-[var(--ink)]">
                              {actor.label}
                            </span>
                            <span className="text-[10px] text-[var(--ink-3)]">
                              {ACTOR_KIND_LABELS[actor.actor_kind] || actor.actor_kind}
                            </span>
                          </div>
                        </Button>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Access level selector */}
          <div>
            <Label className="clara-label mb-1.5 block text-[var(--ink-2)]">
              Access level
            </Label>
            <Select value={permission} onValueChange={setPermission}>
              <SelectTrigger className="text-[13px] h-9 w-full">
                <SelectValue placeholder="Select..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="view">View Only</SelectItem>
                <SelectItem value="edit">Edit</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <DialogFooter>
          <Button
            onClick={() => {
              resetForm()
              onOpenChange(false)
            }}
            variant="outline"
            className="flex-1 cursor-pointer rounded-[4px] border-[0.5px] border-[var(--green)] bg-transparent py-2.5 text-[13px] text-[var(--green)] transition-all duration-150"
          >
            Cancel
          </Button>
          <Button
            onClick={handleGrant}
            disabled={!selectedActor || grantAccess.isPending}
            className="flex-1 cursor-pointer rounded-[4px] bg-[var(--green)] py-2.5 text-[13px] text-white transition-all duration-150 disabled:opacity-40 hover:bg-[var(--green-hover)]"
          >
            {grantAccess.isPending ? 'Granting...' : 'Grant access'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
